// A17: invite landing page. Magic link from a Resend-dispatched
// invite email lands here. Public — anyone with the token can read
// the org name + role; the accept call requires auth (sign-in flow
// kicks in when the user clicks Accept and they don't have a token).

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  acceptInvite,
  ApiError,
  getInvitePublic,
  getToken,
} from "../api/client";

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<{
    org_id: number;
    org_name: string;
    role: string;
    accepted_at: number;
  } | null>(null);
  const [busy, setBusy] = useState<"load" | "accept" | null>("load");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await getInvitePublic(token);
        if (!cancelled) setInfo(resp);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setBusy(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    if (!token) return;
    if (!getToken()) {
      // No session — bounce to sign-in with a return path.
      const dest = encodeURIComponent(`/invite/${token}`);
      navigate(`/signin?return=${dest}`);
      return;
    }
    setBusy("accept");
    setError(null);
    try {
      await acceptInvite(token);
      navigate("/home");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (busy === "load") {
    return (
      <div className="mx-auto mt-12 max-w-md text-center text-sm text-muted">
        Loading invite…
      </div>
    );
  }

  if (!info) {
    return (
      <div className="mx-auto mt-12 max-w-md text-center text-sm text-muted">
        <p>Invite not found or already used.</p>
        {error && <p className="mt-2 text-[0.625rem] text-danger">{error}</p>}
      </div>
    );
  }

  const accepted = info.accepted_at > 0;

  return (
    <main className="mx-auto mt-12 flex max-w-md flex-col gap-4 rounded-panel border border-edge bg-surface-glass p-6">
      <header>
        <h1 className="text-lg font-semibold text-primary">
          Join {info.org_name}
        </h1>
        <p className="mt-1 text-xs text-muted">
          You've been invited as a <span className="font-code">{info.role}</span>.
        </p>
      </header>
      {accepted ? (
        <p className="text-xs text-muted">
          This invite was already accepted. <Link to="/home" className="text-accent hover:underline">Open Waytide →</Link>
        </p>
      ) : (
        <>
          <p className="text-xs text-muted">
            {getToken()
              ? "Click Accept to join the org and land in your workspace list."
              : "Sign in to accept this invite — your existing or new Waytide account binds to the org."}
          </p>
          <button
            type="button"
            onClick={() => void accept()}
            disabled={busy !== null}
            className="rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
          >
            {busy === "accept" ? "Accepting…" : getToken() ? "Accept invite" : "Sign in to accept"}
          </button>
        </>
      )}
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </main>
  );
}
